import { homedir, platform, arch } from 'node:os';
import { ApiError } from './errors.js';

// Optional dependency — loaded lazily and only when a DSN is configured.
type SentryModule = typeof import('@sentry/node');

let sentry: SentryModule | null = null;

function getDsn(): string | undefined {
  if (process.env['REKOR_TELEMETRY_DISABLED']) return undefined;
  return process.env['REKOR_CLI_SENTRY_DSN'] || undefined;
}

// Structural subset — keeps scrubHome decoupled from the optional @sentry/node types.
interface ScrubbableEvent {
  exception?: {
    values?: Array<{
      value?: string;
      stacktrace?: { frames?: Array<{ filename?: string; abs_path?: string }> };
    }>;
  };
}

// Redact the home dir wherever it leaks — fs-error messages embed it, not just stack frames.
export function scrubHome<T extends ScrubbableEvent>(event: T, home: string): T {
  const root = home.replace(/[\\/]+$/, '');
  if (!root) return event;
  const re = new RegExp(root.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?=[\\\\/]|$)', 'g');
  const scrub = (s: string): string => s.replace(re, '~');
  for (const value of event.exception?.values ?? []) {
    if (value.value) value.value = scrub(value.value);
    for (const frame of value.stacktrace?.frames ?? []) {
      if (frame.filename) frame.filename = scrub(frame.filename);
      if (frame.abs_path) frame.abs_path = scrub(frame.abs_path);
    }
  }
  return event;
}

/**
 * Initialize crash reporting. No-op unless `REKOR_CLI_SENTRY_DSN` is set and
 * `REKOR_TELEMETRY_DISABLED` is unset. The `@sentry/node` import is guarded so a missing
 * optional dependency degrades to silence rather than crashing.
 */
export async function initTelemetry(command: string | undefined, version: string): Promise<void> {
  if (sentry) return;
  const dsn = getDsn();
  if (!dsn) return;
  try {
    const mod = await import('@sentry/node');
    const home = homedir();
    mod.init({
      dsn,
      tracesSampleRate: 0,
      release: `rekor-cli@${version}`,
      environment: 'production',
      defaultIntegrations: false,
      beforeSend: (event) => scrubHome(event, home),
    });
    mod.setTag('command', command || 'unknown');
    mod.setTag('node_version', process.version);
    mod.setTag('os_platform', platform());
    mod.setTag('os_arch', arch());
    sentry = mod;
  } catch {
    sentry = null;
  }
}

/** Report an unexpected error. Skips when telemetry is off and skips expected (4xx) API errors. */
export function captureException(err: unknown): void {
  if (!sentry) return;
  if (err instanceof ApiError && err.isExpected) return;
  try {
    sentry.captureException(err);
  } catch {
    // Best-effort.
  }
}

/** Flush and close. Safe to call when telemetry was never initialized. */
export async function closeTelemetry(): Promise<void> {
  if (!sentry) return;
  try {
    await sentry.close(2000);
  } catch {
    // Best-effort.
  }
  sentry = null;
}
